import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../config/axios';
import { formatPickupWindow } from '../utils/dateTimeUtils';

interface OrderItem {
  id: string;
  product_id: string;
  product_name_en: string;
  product_name_zh: string;
  quantity_kg: number;
  price_per_kg: number;
  subtotal: number;
}

interface PickupLocation {
  id: string;
  name: string;
  address: string;
  is_active: boolean;
  created_at: string;
}

interface PickupDate {
  id: string;
  location_id: string;
  pickup_date: string;
  pickup_start_time: string;
  pickup_end_time: string;
  last_order_datetime: string | null;
  location: PickupLocation;
  is_active: boolean;
  created_at: string;
}

interface Order {
  id: string;
  user_id: string | null;
  pickup_date_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address: string;
  total_amount: number;
  status: string;
  is_paid: boolean;
  created_at: string;
  paid_at: string | null;
  items: OrderItem[];
  pickup_date: PickupDate;
}

interface PickupDateWithOrders {
  id: string;
  pickup_date: string;
  pickup_start_time: string;
  pickup_end_time: string;
  location_name: string;
  location_address: string;
  order_count: number;
  total_amount: number;
  paid_count: number;
}

const OrderOverviewPage: React.FC = () => {
  const [selectedPickupDateId, setSelectedPickupDateId] = useState<string | null>(null);
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const language = localStorage.getItem('language') || 'en';

  // Fetch pickup dates with orders
  const { data: pickupDates, isLoading: isLoadingDates, error: errorDates } = useQuery<PickupDateWithOrders[]>({
    queryKey: ['adminPickupDatesWithOrders'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/admin/orders/pickup-dates-with-orders');
      return response.data;
    },
  });

  // Fetch orders for selected pickup date
  const { data: orders, isLoading: isLoadingOrders, error: errorOrders } = useQuery<Order[]>({
    queryKey: ['adminOrders', selectedPickupDateId],
    queryFn: async () => {
      if (!selectedPickupDateId) return [];
      const response = await axiosInstance.get(`/api/admin/orders?pickup_date_id=${selectedPickupDateId}`);
      return response.data;
    },
    enabled: !!selectedPickupDateId,
  });

  const getStatusBadge = (isPaid: boolean) => {
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '14px',
        fontWeight: 'bold',
        backgroundColor: isPaid ? '#28a745' : '#ffc107',
        color: 'white',
      }}>
        {isPaid ? 'Paid' : 'Pending'}
      </span>
    );
  };

  if (isLoadingDates) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Loading pickup dates...</p>
      </div>
    );
  }

  if (errorDates) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p style={{ color: '#dc3545' }}>Failed to load pickup dates. Please try again later.</p>
      </div>
    );
  }

  const handleCleanupAbandoned = async () => {
    if (!confirm('Clean up abandoned orders older than 24 hours? This will cancel unpaid orders and release their inventory.')) {
      return;
    }

    setIsCleaningUp(true);
    try {
      const response = await axiosInstance.post('/api/admin/orders/cleanup-abandoned?hours_old=24');
      alert(`✅ Success! Cleaned up ${response.data.cancelled_count} abandoned order(s)`);
      // Refresh the pickup dates list
      window.location.reload();
    } catch (error: any) {
      alert(`❌ Failed to cleanup orders: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsCleaningUp(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>Order Overview</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={handleCleanupAbandoned}
            disabled={isCleaningUp}
            style={{
              padding: '10px 20px',
              backgroundColor: isCleaningUp ? '#ccc' : '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontWeight: 'bold',
              cursor: isCleaningUp ? 'not-allowed' : 'pointer',
            }}
          >
            {isCleaningUp ? 'Cleaning...' : '🧹 Cleanup Abandoned Orders'}
          </button>
          <a
            href="/admin"
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '4px',
              fontWeight: 'bold',
            }}
          >
            ← Back to Dashboard
          </a>
        </div>
      </div>

      {!selectedPickupDateId ? (
        // Show pickup dates list
        <section>
          <h2 style={{ marginBottom: '20px' }}>Pickup Dates with Orders</h2>
          {pickupDates && pickupDates.length > 0 ? (
            <div style={{ display: 'grid', gap: '15px' }}>
              {pickupDates.map(pickupDate => (
                <div
                  key={pickupDate.id}
                  onClick={() => setSelectedPickupDateId(pickupDate.id)}
                  style={{
                    padding: '20px',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#007bff';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,123,255,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#ddd';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', color: '#007bff' }}>
                        {formatPickupWindow(pickupDate.pickup_date, pickupDate.pickup_start_time, pickupDate.pickup_end_time, 'from', 'to')}
                      </h3>
                      <p style={{ margin: '5px 0', fontSize: '15px', color: '#666' }}>
                        <strong>Location:</strong> {pickupDate.location_name}
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px', color: '#888' }}>
                        {pickupDate.location_address}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right', marginLeft: '20px' }}>
                      <div style={{ 
                        padding: '8px 16px', 
                        backgroundColor: '#007bff', 
                        color: 'white', 
                        borderRadius: '6px',
                        marginBottom: '10px',
                        fontWeight: 'bold',
                        fontSize: '16px'
                      }}>
                        {pickupDate.order_count} {pickupDate.order_count === 1 ? 'Order' : 'Orders'}
                      </div>
                      <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                        <strong>Total:</strong> €{pickupDate.total_amount.toFixed(2)}
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px', color: '#28a745' }}>
                        <strong>Paid:</strong> {pickupDate.paid_count} / {pickupDate.order_count}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <p style={{ fontSize: '18px', color: '#666' }}>
                No pickup dates with orders found.
              </p>
            </div>
          )}
        </section>
      ) : (
        // Show orders for selected pickup date
        <section>
          <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
            <button
              onClick={() => setSelectedPickupDateId(null)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
              }}
            >
              ← Back to Pickup Dates
            </button>
            <h2 style={{ margin: 0 }}>
              Orders for {pickupDates?.find(pd => pd.id === selectedPickupDateId) && 
                formatPickupWindow(
                  pickupDates.find(pd => pd.id === selectedPickupDateId)!.pickup_date,
                  pickupDates.find(pd => pd.id === selectedPickupDateId)!.pickup_start_time,
                  pickupDates.find(pd => pd.id === selectedPickupDateId)!.pickup_end_time,
                  'from',
                  'to'
                )}
            </h2>
          </div>

          {isLoadingOrders ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <p>Loading orders...</p>
            </div>
          ) : errorOrders ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <p style={{ color: '#dc3545' }}>Failed to load orders. Please try again later.</p>
            </div>
          ) : orders && orders.length > 0 ? (
            <div style={{ display: 'grid', gap: '20px' }}>
              {orders.map(order => (
                <div
                  key={order.id}
                  style={{
                    padding: '20px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                  }}
                >
                  {/* Order Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
                    <div>
                      <h3 style={{ margin: '0 0 5px 0' }}>Order #{order.id.slice(0, 8)}</h3>
                      <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                        <strong>Placed:</strong> {new Date(order.created_at).toLocaleString()}
                      </p>
                      {order.is_paid && order.paid_at && (
                        <p style={{ margin: '5px 0', color: '#28a745', fontSize: '14px' }}>
                          <strong>Paid:</strong> {new Date(order.paid_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {getStatusBadge(order.is_paid)}
                      <p style={{ margin: '10px 0 0 0', fontSize: '20px', fontWeight: 'bold' }}>
                        €{order.total_amount.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                    {/* Customer Information */}
                    <div>
                      <h4 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Customer</h4>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>{order.customer_name}</strong>
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                        {order.customer_email}
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                        {order.customer_phone}
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                        {order.customer_address}
                      </p>
                    </div>

                    {/* Pickup Information */}
                    <div>
                      <h4 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Pickup Details</h4>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Date & Time:</strong>
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                        {formatPickupWindow(order.pickup_date.pickup_date, order.pickup_date.pickup_start_time, order.pickup_date.pickup_end_time, 'from', 'to')}
                      </p>
                      <p style={{ margin: '10px 0 5px 0', fontSize: '14px' }}>
                        <strong>Location:</strong>
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                        {order.pickup_date.location.name}
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                        {order.pickup_date.location.address}
                      </p>
                    </div>

                    {/* Order Items */}
                    <div>
                      <h4 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Products</h4>
                      {order.items.map(item => (
                        <div key={item.id} style={{ marginBottom: '8px' }}>
                          <p style={{ margin: '2px 0', fontSize: '14px' }}>
                            <strong>{language === 'zh' ? item.product_name_zh : item.product_name_en}</strong>
                          </p>
                          <p style={{ margin: '2px 0', fontSize: '13px', color: '#666' }}>
                            {item.quantity_kg} kg × €{item.price_per_kg.toFixed(2)}/kg = €{item.subtotal.toFixed(2)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <p style={{ fontSize: '18px', color: '#666' }}>
                No orders found for this pickup date.
              </p>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default OrderOverviewPage;
